import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { FileText, Layers, Table2, BarChart3, Check } from "lucide-react";
import { FadeUp, MaskedReveal } from "./Reveal";

const ACCOUNTANT_IMG =
    "https://images.unsplash.com/photo-1628348068343-c6a848d2b6dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBhY2NvdW50aW5nJTIwZGVzayUyMHRhYmxldHxlbnwwfHx8fDE3ODc4MzMyNDB8MA&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
    {
        num: "01",
        icon: FileText,
        title: "Historische cijfers",
        body: "Importeer in een handomdraai historische cijfers uit een PDF van het jaarverslag. De AI leest, structureert en vult — jij controleert.",
        bullets: ["Jaarverslag-PDF automatisch uitgelezen", "Balans en resultaat direct gestructureerd", "Geen handmatig overtypen meer"],
        visual: "image",
    },
    {
        num: "02",
        icon: Layers,
        title: "Scenariobouwer",
        body: "Bouw ongelimiteerd scenario's, snel en zonder gedoe. Direct de kasstroom-implicaties in beeld.",
        bullets: ["Groeiaannames, investerings- en financieringsplannen", "Inflatie meegenomen in elke doorrekening", "Kasstroom-effect direct zichtbaar"],
    },
    {
        num: "03",
        icon: Table2,
        title: "Afschrijvingsstaat",
        body: "Importeer in een handomdraai de afschrijvingsstaat uit Excel of PDF. Altijd actuele boekwaarden in je scenario's.",
        bullets: ["Werkt met Excel én PDF", "Boekwaarden automatisch bijgewerkt", "Foutloos overgenomen in je model"],
    },
    {
        num: "04",
        icon: BarChart3,
        title: "Monte Carlo Simulator",
        body: "Van gevoel naar kansberekening. Toon statistische kansen, bereken P10–P90 en overtuig met de tornadochart.",
        bullets: ["Statistische kansen in één oogopslag", "Automatische P10–P90 bandbreedte", "Tornadochart voor de gevoeligheidsanalyse"],
        visual: "tornado",
    },
];

const TORNADO_BARS = [
    { label: "Omzetgroei", left: 72, right: 88 },
    { label: "Investering", left: 54, right: 46 },
    { label: "Rentelast", left: 38, right: 30 },
    { label: "Inflatie", left: 26, right: 22 },
];

const TornadoVisual = () => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    return (
    <div ref={ref} data-testid="tornado-visual" className="rounded-2xl border border-black/10 bg-[#0A0A0A] p-8 md:p-10 blue-glow">
        <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/40 mb-2">Gevoeligheidsanalyse</p>
        <p className="font-heading text-white text-2xl font-bold tracking-tight mb-8">Tornadochart</p>
        <div className="space-y-5">
            {TORNADO_BARS.map((bar, i) => (
                <div key={bar.label} className="grid grid-cols-[92px_1fr] items-center gap-4">
                    <span className="text-xs font-bold text-white/50 text-right">{bar.label}</span>
                    <div className="relative h-6 flex items-center">
                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/20" />
                        <motion.div
                            className="absolute right-1/2 h-6 rounded-l-md bg-[#0047FF]/70"
                            initial={{ width: 0 }}
                            animate={inView ? { width: `${Math.min(bar.left, 48)}%` } : {}}
                            transition={{ duration: 1, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] }}
                        />
                        <motion.div
                            className="absolute left-1/2 h-6 rounded-r-md bg-[#FF6B00]"
                            initial={{ width: 0 }}
                            animate={inView ? { width: `${Math.min(bar.right, 48)}%` } : {}}
                            transition={{ duration: 1, delay: 0.15 * i + 0.1, ease: [0.22, 1, 0.36, 1] }}
                        />
                    </div>
                </div>
            ))}
        </div>
        <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-5">
            <span className="text-xs font-bold text-white/40">P10</span>
            <span className="font-heading text-white font-black text-lg">Bandbreedte per aanname</span>
            <span className="text-xs font-bold text-white/40">P90</span>
        </div>
    </div>
    );
};

const Chapter = ({ chapter, reversed }) => {
    const Icon = chapter.icon;
    return (
        <div
            data-testid={`chapter-${chapter.num}`}
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 py-16 md:py-24 border-b border-black/10 last:border-b-0"
        >
            <div className={`lg:col-span-5 ${reversed ? "lg:order-2" : ""}`}>
                <div className="lg:sticky lg:top-32">
                    <FadeUp>
                        <span className="font-heading text-[#0047FF] font-black text-[6rem] md:text-[8rem] leading-none block">
                            {chapter.num}<span className="text-[2.2rem] md:text-[3rem] text-[#0047FF]/40">/04</span>
                        </span>
                    </FadeUp>
                    <FadeUp delay={0.1} className="mt-2 flex items-center gap-4">
                        <span className={`w-12 h-12 rounded-2xl ${reversed ? "bg-[#FF6B00]" : "bg-[#0047FF]"} flex items-center justify-center`}>
                            <Icon className="w-6 h-6 text-white" />
                        </span>
                        <h3 className="font-heading text-[#0A0A0A] font-bold tracking-tight text-2xl sm:text-3xl">
                            {chapter.title}
                        </h3>
                    </FadeUp>
                </div>
            </div>
            <div className={`lg:col-span-7 ${reversed ? "lg:order-1" : ""} flex flex-col justify-center`}>
                <FadeUp delay={0.15}>
                    <p className="text-[#0A0A0A]/70 text-lg leading-relaxed max-w-xl">{chapter.body}</p>
                    <ul className="mt-8 space-y-4">
                        {chapter.bullets.map((b) => (
                            <li key={b} className="flex items-start gap-3">
                                <span className="mt-1 w-5 h-5 rounded-full bg-[#0047FF]/10 flex items-center justify-center shrink-0">
                                    <Check className="w-3.5 h-3.5 text-[#0047FF]" />
                                </span>
                                <span className="text-[#0A0A0A]/80 text-base font-medium">{b}</span>
                            </li>
                        ))}
                    </ul>
                </FadeUp>
                {chapter.visual === "image" && (
                    <FadeUp delay={0.25} className="mt-10">
                        <div className="relative rounded-2xl overflow-hidden border border-black/10">
                            <img src={ACCOUNTANT_IMG} alt="Financieel professional met MeerjarenplanOnline.nl" className="w-full h-64 object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-tr from-[#0047FF]/40 via-transparent to-transparent mix-blend-multiply" />
                        </div>
                    </FadeUp>
                )}
                {chapter.visual === "tornado" && (
                    <FadeUp delay={0.25} className="mt-10">
                        <TornadoVisual />
                    </FadeUp>
                )}
            </div>
        </div>
    );
};

const Chapters = () => (
    <section id="features" data-testid="chapters-section" className="bg-[#F9FAFB] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-8 max-w-3xl">
                <FadeUp>
                    <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#0047FF] mb-5">De vier hoofdstukken</p>
                </FadeUp>
                <h2 className="font-heading text-[#0A0A0A] font-bold tracking-tight text-4xl sm:text-5xl leading-[1.02]">
                    <MaskedReveal>Van ruwe cijfers naar</MaskedReveal>
                    <MaskedReveal delay={0.12}>een verhaal dat <span className="text-[#FF6B00]">overtuigt.</span></MaskedReveal>
                </h2>
            </div>
            {CHAPTERS.map((c, i) => (
                <Chapter key={c.num} chapter={c} reversed={i % 2 === 1} />
            ))}
        </div>
    </section>
);

export default Chapters;
